import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { DialogContentText } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
    IArticleFragment,
    IUpdateLibraryArticleInput,
    UpdateArticleDocument,
    DeleteArticleDocument,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { PageDrawer } from 'common/components/PageDrawer';
import {
    LibraryArticleForm,
    TArticleFormValues,
} from 'library/components/forms/LibraryArticleForm';
import { ApolloError } from 'common/components/ApolloError';
import { AlertDialog } from 'common/components/AlertDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import { getImageId } from 'common/utils/image';
import { formatColor } from 'common/utils/color';

interface IProps {
    pageTitle?: string;
    open?: boolean;
    article: IArticleFragment;
    onUpdate(): void;
    onDelete(): void;
    onClose?(): void;
}

export const LibraryArticleDrawer = ({
    pageTitle,
    open,
    article,
    onUpdate,
    onDelete,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [showOnDeleteAlert, setShowOnDeleteAlert] = useState<boolean>(false);
    const [thumbnailId, setThumbnailId] = useState<string>();
    const [headerImageId, setHeaderImageId] = useState<string>();

    useEffect(() => {
        if (article.thumbnail?.id) setThumbnailId(article.thumbnail.id);

        if (article.headerImage?.id) setHeaderImageId(article.headerImage.id);
    }, [article]);

    const [updateArticle, { error: updateError }] = useMutation(
        UpdateArticleDocument
    );

    const [deleteArticle, { loading: deleteLoading }] = useMutation(
        DeleteArticleDocument,
        {
            update: (cache) => {
                // Remove article from cache
                cache.evict({ id: `LibraryArticle:${article.id}` });
                cache.gc();
            },
        }
    );

    const initialValues: TArticleFormValues = {
        title: article.title,
        description: article.description,
        featured: article.featured,
        titleColor: article.titleColor?.replace('#', '') || '',
        thumbnail: article.thumbnail,
        headerImage: article.headerImage,
    };

    const imageUploaded =
        thumbnailId !== article.thumbnail?.id ||
        headerImageId !== article.headerImage?.id;

    return (
        <PageDrawer open={open} onClose={onClose}>
            {updateError && <ApolloError error={updateError} />}

            {pageTitle && (
                <PageTitle>{`${pageTitle} - ${translate('edit')}`}</PageTitle>
            )}

            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontWeight: 'bold', mt: 0 }} variant="h3">
                        {article.title}
                    </Typography>
                </Box>

                <LibraryArticleForm
                    imageUploaded={imageUploaded}
                    initialValues={initialValues}
                    isDeleting={deleteLoading}
                    onClose={onClose}
                    onDelete={() => {
                        setShowOnDeleteAlert(true);
                    }}
                    onSubmit={(values) => {
                        const {
                            thumbnail,
                            headerImage,
                            titleColor,
                            ...articleInput
                        } = values;

                        const updatedArticle = {
                            ...articleInput,
                            titleColor: formatColor(titleColor),
                            thumbnailId: getImageId(
                                article.thumbnail,
                                thumbnail
                            ),
                            headerImageId: getImageId(
                                article.headerImage,
                                headerImage
                            ),
                        } as IUpdateLibraryArticleInput;

                        return updateArticle({
                            variables: {
                                id: article.id,
                                article: updatedArticle,
                            },
                        }).then(() => {
                            displaySnackbar(
                                translate('libraryArticleActionSuccess.update'),
                                {
                                    variant: 'success',
                                }
                            );

                            onUpdate();
                        });
                    }}
                />
            </Box>

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={deleteLoading}
                            variant="contained"
                            onClick={async () => {
                                try {
                                    await deleteArticle({
                                        variables: { id: article.id },
                                    });
                                } catch (error) {
                                    setShowOnDeleteAlert(false);
                                    showApolloError(error);

                                    return;
                                }

                                setShowOnDeleteAlert(false);

                                displaySnackbar(
                                    translate(
                                        'libraryArticleActionSuccess.delete'
                                    ),
                                    {
                                        variant: 'success',
                                    }
                                );

                                onDelete();
                            }}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={deleteLoading}
                            variant="contained"
                            onClick={() => {
                                setShowOnDeleteAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showOnDeleteAlert}
                title={translate('deleteArticleMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('deleteArticleMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </PageDrawer>
    );
};
